import { useState, useRef, useCallback } from "react";

const DISPOSABLE_DOMAINS = [
  "mailinator.com", "guerrillamail.com", "tempmail.com", "throwaway.email",
  "yopmail.com", "sharklasers.com", "guerrillamailblock.com", "grr.la",
  "dispostable.com", "mailnesia.com", "trashmail.com", "10minutemail.com",
  "temp-mail.org", "fakeinbox.com", "maildrop.cc",
];

interface SpamCheckResult {
  isSpam: boolean;
  score: number;
  reasons: string[];
}

export function useSpamProtection() {
  const [honeypotValue, setHoneypotValue] = useState("");
  const submissionTimestamps = useRef<number[]>([]);
  const formLoadTime = useRef(Date.now());

  const checkHoneypot = useCallback((): boolean => {
    return honeypotValue.length > 0;
  }, [honeypotValue]);

  const checkClientRateLimit = useCallback((maxPerWindow = 3, windowMs = 600_000): boolean => {
    const now = Date.now();
    submissionTimestamps.current = submissionTimestamps.current.filter(
      (t) => now - t < windowMs
    );
    if (submissionTimestamps.current.length >= maxPerWindow) return true;
    submissionTimestamps.current.push(now);
    return false;
  }, []);

  const validateEmail = useCallback((email: string): string | null => {
    if (!email) return null; // optional field
    const trimmed = email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(trimmed)) return "Please enter a valid email address.";
    const domain = trimmed.split("@")[1];
    if (DISPOSABLE_DOMAINS.includes(domain)) return "Please use a non-disposable email address.";
    return null;
  }, []);

  const sanitizeText = useCallback((text: string, maxLength = 2000): string => {
    return text
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
      .replace(/<[^>]+>/g, "")
      .trim()
      .slice(0, maxLength);
  }, []);

  const calculateSpamScore = useCallback((fields: {
    message?: string;
    email?: string;
    honeypotFilled?: boolean;
    timeOnPage?: number;
  }): SpamCheckResult => {
    let score = 0;
    const reasons: string[] = [];

    // Honeypot
    if (fields.honeypotFilled) {
      score += 50;
      reasons.push("honeypot_filled");
    }

    // Time on page (less than 3 seconds is suspicious)
    const elapsed = (fields.timeOnPage ?? Date.now() - formLoadTime.current) / 1000;
    if (elapsed < 3) {
      score += 30;
      reasons.push("too_fast");
    }

    const msg = fields.message || "";
    // Links in message
    const urlCount = (msg.match(/https?:\/\//gi) || []).length;
    if (urlCount > 2) {
      score += 20;
      reasons.push("many_links");
    }

    // Very short message
    if (msg.length > 0 && msg.length < 5) {
      score += 10;
      reasons.push("too_short");
    }

    // Disposable email
    if (fields.email) {
      const domain = fields.email.trim().toLowerCase().split("@")[1];
      if (domain && DISPOSABLE_DOMAINS.includes(domain)) {
        score += 15;
        reasons.push("disposable_email");
      }
    }

    return { isSpam: score >= 50, score, reasons };
  }, []);

  const resetFormLoadTime = useCallback(() => {
    formLoadTime.current = Date.now();
  }, []);

  return {
    honeypotValue,
    setHoneypotValue,
    checkHoneypot,
    checkClientRateLimit,
    validateEmail,
    sanitizeText,
    calculateSpamScore,
    resetFormLoadTime,
    formLoadTime,
  };
}
